import { isSameApi } from '../api/api-url';
import type { HttpClient, RequestOptions } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import { appConfig } from '../config/app-config';
import { accessToken, pendingRenewal, renew, status } from '../session/session.store';

/**
 * The one place a request acquires its proof of identity (005 FR-007), and the one place a 401
 * becomes a renewal (FR-008). Renewal is single-flight in the store, so concurrent 401s cause one.
 *
 * 034 T016: this was `authInterceptor`, registered in `app.config.ts` and applied ambiently. It is
 * a WRAPPER now, installed once with `setAppClient`. The behaviour is the same; what changed is
 * that the bypass is visible — session traffic asks for `directClient()` explicitly rather than
 * being excluded by virtue of which injector it was built from.
 *
 * There is still no list of anonymous routes to keep in step with the server: the session's own
 * traffic (sign-in, renew, sign-out, /me) uses the direct client and never reaches this wrapper,
 * which is also what stops renewal re-entering itself.
 *
 * It is also where every same-origin request declares `X-Time-Zone` (010 FR-001) — the browser's own
 * zone, read fresh per request rather than cached, since a practitioner can change it mid-session
 * without reloading. The preferences read that seeds the effective zone is session traffic in the
 * sense above and bypasses this wrapper entirely, so it never carries this header — that omission is
 * intentional, not a gap.
 */
export interface RenewingClientOptions {
  /**
   * Checked AFTER a successful renewal and BEFORE the one retry — never before, since a renewal that
   * fails or is itself refused already throws without reaching this. A caller whose request belongs
   * to a specific practitioner (`core/outbox/`'s send, pinned to the outbox object it came from) uses
   * this to say so: the renewal itself can be perfectly legitimate — the CURRENT session's own token,
   * genuinely refreshed — while the request being retried was queued for someone who is no longer
   * signed in here at all. Omitted, every caller keeps today's behaviour: retry once, unconditionally.
   */
  readonly mayRetry?: () => boolean;
}

export function createRenewingClient(inner: HttpClient, clientOptions: RenewingClientOptions = {}): HttpClient {
  const send = async <T,>(
    call: (options: RequestOptions) => Promise<T>,
    url: string,
    options: RequestOptions = {},
  ): Promise<T> => {
    if (!isSameApi(url, appConfig().apiBaseUrl)) {
      return call(options);
    }

    const zoned = (token: string | null): RequestOptions => ({
      ...options,
      headers: {
        ...options.headers,
        'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    /**
     * 036 (issue 219, research R-006) — a request about to go out with NO access token waits for a
     * renewal that is ALREADY in flight, rather than being sent bare and discovering the renewal
     * only after a wasted 401. This is what keeps the request count unchanged from `develop` when
     * the connection is good (`e2e/home-scale.spec.ts`'s fixed count): every page-load request
     * issued the instant `restore()`'s fast path settles `unconfirmed` would otherwise go out with
     * no token, get refused, and only then join the very renewal already running — one request
     * doubled into two, times however many the screen makes at once.
     *
     * Only when there is NO token to begin with — a request that already carries one never waits on
     * anything here, exactly as before.
     */
    let firstToken = accessToken.get();
    if (firstToken === null) {
      const pending = pendingRenewal();
      if (pending) {
        await pending;
        firstToken = accessToken.get();
        if (firstToken === null && status.get() === 'unconfirmed') {
          // The renewal this request waited for could not reach the server either. It was never
          // sent, and sending it now would only repeat the same wait a moment later — R-006's
          // "fails as unreachable, without being sent."
          throw new HttpFailure({ status: 0, statusText: 'Network request failed', url, error: null });
        }
        // A renewal that ended SIGNED OUT falls through: sending with no token gets the same 401 a
        // request made without this wait would have gotten, and the retry logic below already
        // handles it exactly as it always has.
      }
    }

    try {
      return await call(zoned(firstToken));
    } catch (error) {
      if (!(error instanceof HttpFailure) || error.status !== 401) throw error;

      // Single-flight in the store: concurrent 401s await one renewal between them.
      const renewed = await renew();
      if (!renewed) throw error;

      // The renewal succeeded, but for a request pinned to a specific holder (the outbox's send),
      // that alone does not mean the retry is still this holder's to make — the CURRENT session can
      // have renewed validly for someone else entirely while this request waited. Checked only now,
      // never earlier: a caller with no opinion (`mayRetry` omitted) keeps the old, unconditional retry.
      if (clientOptions.mayRetry && !clientOptions.mayRetry()) throw error;

      // Exactly one retry. A second 401 after a successful renewal is a real refusal — retrying
      // again would loop against a server that has already answered.
      return call(zoned(accessToken.get()));
    }
  };

  return {
    get: (url, options) => send((o) => inner.get(url, o), url, options),
    post: (url, body, options) => send((o) => inner.post(url, body, o), url, options),
    put: (url, body, options) => send((o) => inner.put(url, body, o), url, options),
    patch: (url, body, options) => send((o) => inner.patch(url, body, o), url, options),
    delete: (url, options) => send((o) => inner.delete(url, o), url, options),
  };
}
